/**
 * State for SHXRE's Twitter-inspired feed algorithm.
 *
 * Stores client-side signals that feed into the scoring pipeline:
 *  - Negative feedback (Twitter's FeedbackFatigueScorer)
 *  - Dwell time per post (Twitter's PredictedDwellScore)
 *  - Served authors in session (Twitter's ImpressedAuthorDecayRescoring)
 */

// ---------------------------------------------------------------------------
// Negative feedback (Twitter's "See fewer" / "Not interested")
// ---------------------------------------------------------------------------

export type NegativeFeedbackEntry = {
  type: 'not_interested' | 'see_fewer'
  targetDid?: string   // author DID for "see fewer from this author"
  targetUri?: string   // post URI for "not interested in this post"
  timestamp: number
}

const FEEDBACK_WINDOW = 200
const FEEDBACK_DECAY_DAYS = 140 // Twitter uses ~140 day decay

const negativeFeedback: NegativeFeedbackEntry[] = []

export function addNegativeFeedback(entry: Omit<NegativeFeedbackEntry, 'timestamp'>) {
  negativeFeedback.push({...entry, timestamp: Date.now()})
  if (negativeFeedback.length > FEEDBACK_WINDOW) {
    negativeFeedback.splice(0, negativeFeedback.length - FEEDBACK_WINDOW)
  }
}

export function getNegativeFeedback(): NegativeFeedbackEntry[] {
  return negativeFeedback
}

/**
 * Compute penalty multiplier for a post based on negative feedback.
 * Twitter's FeedbackFatigueScorer applies time-decayed penalties:
 *  - Fresh feedback: 0.2× (strong penalty)
 *  - Old feedback: approaches 1.0× over ~140 days
 */
export function computeFeedbackPenalty(
  authorDid: string,
  postUri: string,
): number {
  let minMultiplier = 1.0

  for (const entry of negativeFeedback) {
    const daysSince = (Date.now() - entry.timestamp) / (1000 * 60 * 60 * 24)
    const decay = Math.min(daysSince / FEEDBACK_DECAY_DAYS, 1.0)
    const multiplier = 0.2 + 0.8 * decay

    if (entry.type === 'not_interested' && entry.targetUri === postUri) {
      minMultiplier = Math.min(minMultiplier, 0.05) // almost completely hidden
    }
    if (entry.type === 'see_fewer' && entry.targetDid === authorDid) {
      minMultiplier = Math.min(minMultiplier, multiplier)
    }
  }

  return minMultiplier
}

// ---------------------------------------------------------------------------
// Dwell time tracking (Twitter's PredictedDwellScore)
// ---------------------------------------------------------------------------

export type DwellEntry = {
  uri: string
  authorDid: string
  dwellMs: number
  timestamp: number
}

const DWELL_WINDOW = 200
const dwellHistory: DwellEntry[] = []

export function recordDwell(uri: string, authorDid: string, dwellMs: number) {
  dwellHistory.push({uri, authorDid, dwellMs, timestamp: Date.now()})
  if (dwellHistory.length > DWELL_WINDOW) {
    dwellHistory.splice(0, dwellHistory.length - DWELL_WINDOW)
  }
}

export function getDwellHistory(): DwellEntry[] {
  return dwellHistory
}

/**
 * Get average dwell time for an author's posts (proxy for content quality).
 * Higher dwell = user finds this author's content interesting.
 */
export function getAuthorAvgDwell(authorDid: string): number | null {
  const entries = dwellHistory.filter(e => e.authorDid === authorDid)
  if (entries.length === 0) return null
  const total = entries.reduce((sum, e) => sum + e.dwellMs, 0)
  return total / entries.length
}

// ---------------------------------------------------------------------------
// Served authors tracking (Twitter's ImpressedAuthorDecayRescoring)
// ---------------------------------------------------------------------------

const servedAuthors = new Map<string, number>()

export function recordServedAuthor(did: string) {
  servedAuthors.set(did, (servedAuthors.get(did) ?? 0) + 1)
}

export function getServedAuthors(): Map<string, number> {
  return servedAuthors
}

export function resetServedAuthors() {
  servedAuthors.clear()
}

/**
 * Get how many times an author has been served in this session.
 */
export function getAuthorServedCount(did: string): number {
  return servedAuthors.get(did) ?? 0
}
